<template>
	<component :is="tag" v-bind="linkProps" @click="handleClick" :type="props.type" class="btn"
			   :disabled="isDisabled" ref="buttonElement"
			   :class="[colorClass ? 'btn-' + colorClass : '', colorModifierClass, roundableString, sizeableString ? 'btn-' + sizeableString : '', hasActiveTo && props.activeColor ? 'btn-' + props.activeColor : '', {'btn--active': hasActiveTo, 'btn--disabled': isDisabled, 'btn-shadow': props.shadow, 'btn-text': props.text, 'btn-outlined': props.outlined, 'btn-fab': props.fab, 'btn-icon': props.icon, 'btn-plain': props.plain, 'text-white': colorHex && !props.outlined && !props.text}]"
			   :style="style">
		<loading-icon v-if="showLoadingIcon"/>
		<template v-else>
			<slot/>
		</template>
	</component>
</template>

<script setup lang="ts">
import {type LinkableProps, useLinkable} from "../../composables/useLinkable";
import {type ButtonComponentProps, useButtonComponent} from "../../composables/useButtonComponent";
import {computed} from 'vue';

const props = withDefaults(defineProps<{
	activeColor?: string,
} & ButtonComponentProps & LinkableProps>(), {
	rounded: true,
	type: 'button'
});

const emit = defineEmits(['update:loading', 'click']);
const {
	style,
	isDisabled,
	showLoadingIcon,
	colorClass, colorModifierClass, colorHex,
	roundableString, sizeableString
} = useButtonComponent(props, emit);
const {hasActiveTo, linkProps} = useLinkable(props);

const tag = computed(() => {
	if (linkProps.value) {
		return resolveComponent('NuxtLink');
	}

	return 'button';
});

function handleClick(event) {
	if (!props.disabled) {
		emit('click', event)
	}
}

</script>

<style>
/*********************
  Inputs
**********************/
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
/* ==========================================================================
   Mixins
   ========================================================================== */
.app .btn {
  height: 40px;
  min-width: 40px;
  font-size: 13px;
  padding: 4px 20px;
  background-color: #f4f4f5;
  border: 1px solid transparent;
  box-sizing: border-box;
  color: #5e6b6b;
  font-family: nunito-sans, Arial, Helvetica, sans-serif;
  text-transform: none;
  letter-spacing: 0.1px;
  outline: 0 !important;
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  font-weight: 500;
  justify-content: center;
  position: relative;
  text-decoration: none;
  transition-duration: 0.28s;
  transition-property: box-shadow, transform, opacity;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  vertical-align: middle;
  white-space: nowrap;
  cursor: pointer;
  line-height: 1.2;
  -webkit-appearance: none !important;
}
.app .btn:before {
  border-radius: inherit;
  bottom: 0;
  color: inherit;
  content: "";
  left: 0;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.6, 1);
  background-color: currentColor;
}
.app .btn:hover {
  color: #5e6b6b;
}
.app .btn > .icon:first-child:not(:only-child) + * {
  margin-left: 8px;
}
.app .btn > .icon:last-child:not(:only-child) {
  margin-left: 8px;
}
.app .btn .icon, .app .btn .icon > svg {
  height: 14px;
  width: 14px;
}
.app .btn .icon:only-child, .app .btn .icon:only-child > svg {
  height: 18.2px;
  width: 18.2px;
}
.app .btn:disabled, .app .btn.btn--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.app .btn.btn-text, .app .btn.btn-plain, .app .btn.btn-outlined {
  background-color: transparent;
  color: currentColor;
}
.app .btn.btn-outlined {
  border-color: currentColor;
}
.app .btn.btn-icon {
  padding: 4px !important;
}
.app .btn.btn-icon.rounded-full {
  padding: 0 !important;
}
.app .btn.btn-plain:before {
  opacity: 0 !important;
}
.app .btn.btn-plain:not(:disabled, .btn--disabled):hover {
  color: #008200;
}
.app .btn.btn-white {
  color: #465050;
  background-color: #ffffff !important;
}
.app .btn.btn-white.btn-text, .app .btn.btn-white.btn-plain, .app .btn.btn-white.btn-outlined {
  background-color: transparent !important;
  color: #ffffff;
}
.app .btn.btn-black {
  color: #ffffff;
  background-color: #2e3535 !important;
}
.app .btn.btn-black.btn-text, .app .btn.btn-black.btn-plain, .app .btn.btn-black.btn-outlined {
  background-color: transparent !important;
  color: #2e3535;
}
.app .btn.btn-primary {
  color: #ffffff;
  background-color: #008200 !important;
}
.app .btn.btn-primary.btn-text, .app .btn.btn-primary.btn-plain, .app .btn.btn-primary.btn-outlined {
  background-color: transparent !important;
  color: #008200;
}
.app .btn.btn-secondary {
  color: #ffffff;
  background-color: #f0820a !important;
}
.app .btn.btn-secondary.btn-text, .app .btn.btn-secondary.btn-plain, .app .btn.btn-secondary.btn-outlined {
  background-color: transparent !important;
  color: #f0820a;
}
.app .btn.btn-error {
  color: #ffffff;
  background-color: #FA5555 !important;
}
.app .btn.btn-error.btn-text, .app .btn.btn-error.btn-plain, .app .btn.btn-error.btn-outlined {
  background-color: transparent !important;
  color: #FA5555;
}
.app .btn.btn-success {
  color: #ffffff;
  background-color: #47a447 !important;
}
.app .btn.btn-success.btn-text, .app .btn.btn-success.btn-plain, .app .btn.btn-success.btn-outlined {
  background-color: transparent !important;
  color: #47a447;
}
.app .btn.btn-warning {
  color: #ffffff;
  background-color: #EB9E05 !important;
}
.app .btn.btn-warning.btn-text, .app .btn.btn-warning.btn-plain, .app .btn.btn-warning.btn-outlined {
  background-color: transparent !important;
  color: #EB9E05;
}
.app .btn.btn-transparent {
  color: #ffffff;
  background-color: transparent !important;
}
.app .btn.btn-transparent.btn-text, .app .btn.btn-transparent.btn-plain, .app .btn.btn-transparent.btn-outlined {
  background-color: transparent !important;
  color: transparent;
}
.app .btn.btn-xs {
  min-width: 26px;
  height: 26px;
  font-size: 11.5px;
  padding: 4px 12px;
}
.app .btn.btn-xs .icon, .app .btn.btn-xs .icon > svg {
  height: 13.8px;
  width: 13.8px;
}
.app .btn.btn-xs .icon:only-child, .app .btn.btn-xs .icon:only-child > svg {
  height: 14.95px;
  width: 14.95px;
}
.app .btn.btn-sm {
  min-width: 34px;
  height: 34px;
  font-size: 12px;
  padding: 4px 16px;
}
.app .btn.btn-sm .icon, .app .btn.btn-sm .icon > svg {
  height: 14.4px;
  width: 14.4px;
}
.app .btn.btn-sm .icon:only-child, .app .btn.btn-sm .icon:only-child > svg {
  height: 15.6px;
  width: 15.6px;
}
.app .btn.btn-fab {
  min-width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 100% !important;
  background: #bebebe;
}
.app .btn.btn-fab .icon, .app .btn.btn-fab .icon > svg {
  height: 16px;
  width: 16px;
  color: #ffffff;
}
.app .btn.btn-lg {
  min-width: 48px;
  height: 48px;
  font-size: 14px;
}
.app .btn.btn-lg .icon, .app .btn.btn-lg .icon > svg {
  height: 16.8px;
  width: 16.8px;
}
.app .btn.btn-lg .icon:only-child, .app .btn.btn-lg .icon:only-child > svg {
  height: 18.2px;
  width: 18.2px;
}
.app .btn.btn-xl {
  min-width: 52px;
  height: 52px;
  font-size: 16px;
}
.app .btn.btn-xl .icon, .app .btn.btn-xl .icon > svg {
  height: 19.2px;
  width: 19.2px;
}
.app .btn.btn-xl .icon:only-child, .app .btn.btn-xl .icon:only-child > svg {
  height: 20.8px;
  width: 20.8px;
}
.app .btn.btn-shadow {
  box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
}
.app .btn.rounded-lg {
  border-radius: 28px !important;
}
.app .btn.lighten:before {
  background-color: white;
  opacity: 0.16;
}
.app .btn:not(:disabled, .btn--disabled):hover:before {
  opacity: 0.04;
}
.app .btn.btn--active:before, .app .btn:active:before {
  opacity: 0.12;
}
.app .btn:focus:before {
  opacity: 0.16;
}
</style>